'use client';

import React, { createContext, useContext, useState, type ReactNode } from 'react';
import { createDebug } from '../utils/debug';

type DebugContextType = ReturnType<typeof createDebug>;

const DebugContext = createContext<DebugContextType | null>(null);

type DebugProviderProps = {
  isEnabled: boolean;
  children: ReactNode;
};

/**
 * DebugProvider - Provides debug configuration and logging functions via React context
 *
 * This allows any component in the app to access debug functionality:
 * - Check if debug mode is enabled
 * - Use conditional debug logging
 * - Access force logging for critical issues
 *
 * `isEnabled` is derived from server-side config (env vars) and is fixed for the session, so the
 * debug API is created once via `useState` and is referentially stable for the provider's lifetime.
 */
function DebugProvider({ isEnabled, children }: DebugProviderProps): React.JSX.Element {
  const [debugApi] = useState(() => createDebug({ isEnabled }));

  return <DebugContext.Provider value={debugApi}>{children}</DebugContext.Provider>;
}

/**
 * Hook to access the debug API from any component within a DebugProvider.
 *
 * The returned API is referentially stable, so it is safe to include in hook dependency arrays.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const debug = useDebug();
 *
 *   useEffect(() => {
 *     debug.log('Component mounted');
 *
 *     if (debug.isEnabled()) {
 *       // Only do expensive debug operations when enabled
 *       performDetailedLogging();
 *     }
 *   }, [debug]);
 *
 *   return <div>My Component</div>;
 * }
 * ```
 *
 * @returns Debug API object with logging functions and enabled state
 * @throws Error if used outside of DebugProvider
 */
function useDebug(): DebugContextType {
  const context = useContext(DebugContext);

  if (!context) {
    throw new Error('useDebug must be used within a DebugProvider. Make sure your component is wrapped with a DebugProvider.');
  }

  return context;
}

export { DebugProvider, useDebug, type DebugContextType };
